import React, { useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import { Power } from 'lucide-react'
import * as THREE from 'three'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Nguồn điện & mạch kín (Lớp 11 — Bài 17) — mô hình 3D: nguồn → công tắc → bóng đèn.
// Đóng công tắc → electron chạy trong mạch kín → đèn sáng. Mở → mạch hở → đèn tắt.
const CR = [new THREE.Vector3(-3.4, -1.7, 0), new THREE.Vector3(-3.4, 1.7, 0), new THREE.Vector3(3.4, 1.7, 0), new THREE.Vector3(3.4, -1.7, 0)]
const SEG = CR.map((p, i) => CR[(i + 1) % 4].clone().sub(p).length())
const PERIM = SEG.reduce((a, b) => a + b, 0)
function ptAt(dist: number) {
  let d = ((dist % PERIM) + PERIM) % PERIM
  for (let i = 0; i < 4; i++) { if (d <= SEG[i]) { const p = CR[i], q = CR[(i + 1) % 4]; return p.clone().lerp(q, d / SEG[i]) } d -= SEG[i] }
  return CR[0].clone()
}

function Wire({ a, b }: { a: THREE.Vector3; b: THREE.Vector3 }) {
  const mid = a.clone().add(b).multiplyScalar(0.5), len = a.distanceTo(b)
  const horiz = Math.abs(b.x - a.x) > Math.abs(b.y - a.y)
  return <mesh position={[mid.x, mid.y, 0]}><boxGeometry args={horiz ? [len, 0.12, 0.12] : [0.12, len, 0.12]} /><meshStandardMaterial color="#94a3b8" transparent opacity={0.5} metalness={0.3} roughness={0.5} /></mesh>
}

function Electrons({ closed, volt }: { closed: boolean; volt: number }) {
  const grp = useRef<THREE.Group>(null)
  const off = useRef(0)
  const N = 24
  useFrame(() => {
    if (closed) off.current += 0.02 + volt * 0.012
    const g = grp.current; if (!g) return
    g.visible = closed
    g.children.forEach((c, i) => { const p = ptAt((i / N) * PERIM + off.current); (c as THREE.Mesh).position.set(p.x, p.y, 0) })
  })
  return <group ref={grp}>{Array.from({ length: N }).map((_, i) => (
    <mesh key={i}><sphereGeometry args={[0.11, 12, 12]} /><meshStandardMaterial color="#2563eb" emissive="#3b82f6" emissiveIntensity={0.8} /></mesh>
  ))}</group>
}

function Bulb({ on, volt }: { on: boolean; volt: number }) {
  const glow = on ? Math.min(1, volt / 9) : 0
  return (
    <group position={[0, 1.7, 0]}>
      {on && <pointLight position={[0, 0.2, 0.6]} intensity={glow * 2.2} distance={5} color="#fde68a" />}
      <mesh position={[0, 0.35, 0]}><sphereGeometry args={[0.42, 24, 24]} />
        <meshStandardMaterial color={on ? '#fff7cc' : '#e2e8f0'} emissive={on ? '#fde047' : '#000000'} emissiveIntensity={glow} transparent opacity={0.9} />
      </mesh>
      <mesh position={[0, -0.05, 0]}><cylinderGeometry args={[0.16, 0.16, 0.34, 16]} /><meshStandardMaterial color="#9ca3af" metalness={0.7} roughness={0.4} /></mesh>
      <Html position={[0, 0.95, 0]} center><span style={{ color: '#b45309', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>Bóng đèn</span></Html>
    </group>
  )
}

function Battery() {
  return (
    <group position={[-3.4, 0, 0]}>
      <mesh><cylinderGeometry args={[0.34, 0.34, 1.5, 28]} /><meshStandardMaterial color="#cbd5e1" transparent opacity={0.2} metalness={0.2} roughness={0.6} depthWrite={false} /></mesh>
      <mesh position={[0, 0.85, 0]}><cylinderGeometry args={[0.12, 0.12, 0.2, 16]} /><meshStandardMaterial color="#ef4444" /></mesh>
      <Html position={[-0.7, 0.6, 0]} center><span style={{ color: '#dc2626', fontWeight: 800 }}>+</span></Html>
      <Html position={[-0.7, -0.6, 0]} center><span style={{ color: '#1d4ed8', fontWeight: 800 }}>−</span></Html>
      <Html position={[-0.95, 0, 0]} center><span style={{ color: '#475569', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>Nguồn</span></Html>
    </group>
  )
}

function Switch({ closed, onToggle }: { closed: boolean; onToggle: () => void }) {
  return (
    <group position={[3.4, 0, 0]}>
      {/* vùng click vô hình để ĐÓNG/MỞ công tắc bằng tay */}
      <mesh onClick={(e) => { e.stopPropagation(); onToggle() }}
        onPointerOver={() => { document.body.style.cursor = 'pointer' }}
        onPointerOut={() => { document.body.style.cursor = 'default' }}>
        <boxGeometry args={[1.3, 1.8, 0.6]} /><meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      <mesh position={[0, -0.55, 0]}><sphereGeometry args={[0.12, 12, 12]} /><meshStandardMaterial color="#0f172a" /></mesh>
      <mesh position={[0, 0.55, 0]}><sphereGeometry args={[0.12, 12, 12]} /><meshStandardMaterial color="#0f172a" /></mesh>
      {/* cần gạt: đóng → thẳng đứng nối 2 chốt; mở → chếch ra */}
      <group position={[0, -0.55, 0]} rotation={[0, 0, closed ? 0 : -0.7]}>
        <mesh position={[0, 0.55, 0]}><boxGeometry args={[0.1, 1.1, 0.1]} /><meshStandardMaterial color={closed ? '#16a34a' : '#dc2626'} /></mesh>
      </group>
      <Html position={[0.85, 0, 0]} center><span style={{ color: closed ? '#16a34a' : '#dc2626', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{closed ? 'Đóng' : 'Mở'}</span></Html>
    </group>
  )
}

function CircuitScene({ closed, volt, onToggle }: { closed: boolean; volt: number; onToggle: () => void }) {
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[4, 6, 6]} intensity={0.7} />
      {/* dây: trên, dưới liền; cạnh trái ẩn đoạn trong nguồn (2 stub tới 2 cực pin); cạnh phải tách tại công tắc */}
      <Wire a={CR[1]} b={CR[2]} />
      <Wire a={CR[3]} b={CR[0]} />
      <Wire a={new THREE.Vector3(-3.4, 1.7, 0)} b={new THREE.Vector3(-3.4, 0.75, 0)} />
      <Wire a={new THREE.Vector3(-3.4, -0.75, 0)} b={new THREE.Vector3(-3.4, -1.7, 0)} />
      <Wire a={new THREE.Vector3(3.4, 1.7, 0)} b={new THREE.Vector3(3.4, 0.55, 0)} />
      <Wire a={new THREE.Vector3(3.4, -0.55, 0)} b={new THREE.Vector3(3.4, -1.7, 0)} />
      <Battery />
      <Bulb on={closed} volt={volt} />
      <Switch closed={closed} onToggle={onToggle} />
      <Electrons closed={closed} volt={volt} />
      <OrbitControls enablePan={false} minDistance={6} maxDistance={16} />
    </>
  )
}

const CircuitSimulation: React.FC<Props> = ({
  title = 'Nguồn điện và mạch điện kín (3D)',
  description = 'Bấm vào CÔNG TẮC (hoặc nút bên dưới) để đóng/mở. Đóng → dòng điện chạy, đèn sáng; mở → mạch hở, đèn tắt. Kéo chuột để xoay mô hình.',
  onComplete,
}) => {
  const [closed, setClosed] = useState(true)
  const [volt, setVolt] = useState(6)
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }
  const toggle = () => { touch(); setClosed((c) => !c) }
  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-4 my-3">
      <h4 className="font-bold text-amber-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="rounded-lg overflow-hidden border border-gray-100" style={{ height: 320, background: 'linear-gradient(#f8fafc,#eef2f7)' }}>
        <Canvas camera={{ position: [0, 0.5, 11], fov: 50 }} onCreated={touch}>
          <CircuitScene closed={closed} volt={volt} onToggle={toggle} />
        </Canvas>
      </div>
      <div className="flex items-center gap-3 mt-3 text-sm">
        <button onClick={toggle} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-bold ${closed ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}`}><Power className="w-4 h-4" /> {closed ? 'Công tắc: ĐÓNG' : 'Công tắc: MỞ'}</button>
        <label className="flex items-center gap-2 flex-1"><span className="w-24 text-gray-600">Hiệu điện thế</span><input type="range" min={1} max={9} step={1} value={volt} onChange={(e) => { touch(); setVolt(Number(e.target.value)) }} className="flex-1 accent-amber-500" /><span className="w-10 text-right font-semibold">{volt}V</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Electron (chấm xanh) dịch chuyển trong mạch khi công tắc đóng. Tăng hiệu điện thế → electron chạy nhanh hơn, đèn sáng hơn.</p>
    </div>
  )
}
export default CircuitSimulation
